import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import MapPin, { MapDot } from "@/components/MapPin";
import { mapUrl } from "@/lib/api";
import { placeDots, placePins } from "@/lib/mapframe";

// Full-screen zoom on the 1280x1280 Static Map (640x640 @2) from our proxy. No Maps JavaScript API.
export default function MapFullscreen({ radarId, markers, dots = [], cards = [], unlocked = false, version, theme, onClose }) {
  const [scale, setScale] = useState(1);
  const pins = useMemo(() => placePins(markers, 640, 640, dots), [markers, dots]);
  const placedDots = useMemo(() => placeDots(markers, dots, 640, 640), [markers, dots]);
  const nameOf = (label) => cards.find((c) => String(c.rank) === String(label))?.name;
  const layout = useMemo(() => {
    // Label boxes at zoom 1: flip left near the right edge; if two overlap, move the later one above/below its pin.
    const side = Math.min(window.innerWidth, window.innerHeight);
    const placed = [];
    const out = {};
    pins.filter((p) => !p.is_user).forEach((p) => {
      const w = Math.min(160, (nameOf(p.label) || "").length * 6.5 + 14);
      const px = (p.x / 100) * side;
      const py = (p.y / 100) * side;
      const flip = px + 190 > side;
      const x0 = flip ? px - 15 - w : px + 15;
      let dy = 0;
      for (const cand of [0, -30, 30, -60, 60]) {
        const y0 = py - 32 + cand;
        if (!placed.some((b) => x0 < b.x1 && x0 + w > b.x0 && y0 < b.y1 && y0 + 20 > b.y0)) {
          dy = cand;
          break;
        }
      }
      placed.push({ x0, x1: x0 + w, y0: py - 32 + dy, y1: py - 12 + dy });
      out[p.label] = { flip, dy };
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins, cards]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // iOS Safari: keep pinch on the map surface, never zoom the page behind
    const stop = (e) => e.preventDefault();
    const gestures = ["gesturestart", "gesturechange", "gestureend"];
    gestures.forEach((g) => document.addEventListener(g, stop, { passive: false }));
    return () => {
      window.removeEventListener("keydown", onKey);
      gestures.forEach((g) => document.removeEventListener(g, stop));
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const side = "min(100vw, 100dvh)";
  return createPortal(
    <div data-testid="map-fullscreen" role="dialog" aria-modal="true" aria-label="Carte en plein écran" className="fixed inset-0 z-[70] bg-[color:var(--dot-bg)]" style={{ touchAction: "none" }}>
      <TransformWrapper
        minScale={1}
        maxScale={3}
        initialScale={1}
        centerOnInit
        doubleClick={{ step: 0.9 }}
        wheel={{ step: 0.12 }}
        pinch={{ step: 5 }}
        onTransformed={(_, s) => setScale(s.scale)}
      >
        <TransformComponent
          wrapperStyle={{ width: "100vw", height: "100dvh", touchAction: "none" }}
          contentStyle={{ width: "100vw", height: "100dvh", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <div className="relative" style={{ width: side, height: side }} data-testid="map-fullscreen-canvas">
            <img
              src={mapUrl(radarId, theme, version, 640, 640)}
              alt="Carte des architectes en grand"
              data-testid="map-fullscreen-image"
              className="block h-full w-full select-none"
              draggable={false}
            />
            {placedDots.map((d) =>
              unlocked ? (
                <MapPin key={`d${d.rank}`} pin={{ ...d, label: String(d.rank) }} small scale={1 / scale} />
              ) : (
                <MapDot key={`d${d.rank}`} dot={d} scale={1 / scale} />
              ),
            )}
            {pins.map((p) => (
              <MapPin key={p.label} pin={p} scale={1 / scale} name={p.is_user ? null : nameOf(p.label)} flip={p.is_user ? undefined : layout[p.label]?.flip} labelDy={p.is_user ? 0 : layout[p.label]?.dy || 0} />
            ))}
          </div>
        </TransformComponent>
      </TransformWrapper>
      <button
        type="button"
        onClick={onClose}
        data-testid="map-fullscreen-close"
        aria-label="Fermer la carte"
        className="safe-top absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full border border-line bg-surface text-ink shadow-modal"
      >
        <X className="h-5 w-5" strokeWidth={1.75} />
      </button>
      <span
        data-testid="map-fullscreen-attribution"
        className="pointer-events-none absolute bottom-3 left-3 rounded-full bg-surface/90 px-2.5 py-1 text-[11px] font-medium text-muted"
        style={{ marginBottom: "env(safe-area-inset-bottom)" }}
      >
        Carte et données : Google
      </span>
    </div>,
    document.body,
  );
}
